import { z } from 'zod';
import { PopulatedUserSchema } from '../user/user.schema';
import { PopulatedDepartmentSchema } from '../department/department.schema';
// Namespace import + z.lazy defers the cross-reference past the
// process ↔ process-step-recording module cycle (same pattern as survey.schema).
import * as recordingModule from '../process-step-recording/process-step-recording.schema';
/**
 * Process Category Schema
 * Classifies processes for process landscape map layout
 */ export const ProcessCategorySchema = z.enum([
    'core',
    'management',
    'support'
]);
/**
 * Process Recording Schema
 * Represents recording information for a process. The start event itself
 * (name/type/description) lives on the start event NODE (ADR 0003) and is
 * served via `ShowProcessResponse.events`.
 */ export const ProcessRecordingSchema = z.object({
    startDate: z.number().optional(),
    endDate: z.number().optional()
});
/**
 * Process Status Schema
 * The two-stage completion verdict: stage A is the stored `recording.endDate`,
 * stage B is derived at read time from the process' still-open survey rows.
 */ export const ProcessStatusSchema = z.enum([
    'not_started',
    'in_recording',
    'recording_complete',
    'completed'
]);
/**
 * Derived Input Port Schema
 * An input port derived at query time from process recording data
 */ export const DerivedInputPortSchema = z.object({
    portKey: z.string(),
    name: z.string(),
    description: z.string().optional()
});
/**
 * Derived Output Port Schema
 * An output port derived at query time from terminal recording steps
 */ export const DerivedOutputPortSchema = z.object({
    portKey: z.string(),
    name: z.string(),
    description: z.string().optional(),
    output: z.string().optional(),
    stepId: z.string(),
    ruleId: z.string().optional()
});
/**
 * Process Schema
 * Represents a process entity in the organization
 */ export const ProcessSchema = z.object({
    _id: z.string(),
    name: z.string(),
    description: z.string(),
    owner: PopulatedUserSchema,
    // Optional accountable person; a dangling ref populates to null in Mongoose,
    // so null must parse (.nullish, not .optional)
    accountable: PopulatedUserSchema.nullish(),
    createdBy: PopulatedUserSchema,
    department: PopulatedDepartmentSchema,
    apqcEquivalentId: z.string().optional(),
    category: ProcessCategorySchema.optional(),
    recording: ProcessRecordingSchema.optional(),
    dueDate: z.number().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    deletedAt: z.number().optional()
});
/**
 * Populated Process Schema
 * Used when process references are populated with simplified process data
 * Simplified version for populated references in API responses
 */ export const PopulatedProcessSchema = z.object({
    _id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    owner: z.union([
        z.string(),
        PopulatedUserSchema
    ]),
    department: z.union([
        z.string(),
        PopulatedDepartmentSchema
    ]),
    recording: ProcessRecordingSchema.optional()
});
/**
 * Process With Steps Schema
 * Extended process schema that includes process step recordings
 * Used in show process response to include full process details
 * Uses ProcessStepRecordingDisplaySchema for fully populated nested objects (helpers, successors, etc.)
 */ export const ProcessWithStepsSchema = z.lazy(()=>// Display schema because show-process needs fully populated data for rendering.
    ProcessSchema.extend({
        processStepRecordings: z.array(recordingModule.ProcessStepRecordingDisplaySchema).optional()
    }));

//# sourceMappingURL=process.schema.js.map